import { RecruitmentStatus } from "@/lib/schema";
import { getRecruitmentStatusOptions, RECRUITMENT_STATUS_ICONS } from "@/lib/recruitmentStatusCopy";
import { Label } from "@/components/ui/label";

interface RecruitmentStatusSelectorProps {
  /** Current selected value */
  value: RecruitmentStatus;
  /** Change handler */
  onChange: (status: RecruitmentStatus) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether this is edit mode (shows highlighted container) */
  isEditMode?: boolean;
}

export function RecruitmentStatusSelector({
  value,
  onChange,
  disabled = false,
  isEditMode = false,
}: RecruitmentStatusSelectorProps) {
  const options = getRecruitmentStatusOptions();

  const content = (
    <div className="space-y-2">
      <Label className="text-sm font-semibold space-grotesk block">
        Recruitment Status <span className="text-red-500">*</span>
      </Label>
      <p className="text-sm text-gray-600 mb-3 font-manrope">
        {isEditMode 
          ? "Update the recruitment status based on current hiring needs. This will affect how the job appears to referrers."
          : "Set the initial status for this job. This can be changed later."}
      </p>

      <div className="space-y-3">
        {options.map(({ value: optionValue, config }) => {
          const IconComponent = RECRUITMENT_STATUS_ICONS[config.icon];
          const isSelected = value === optionValue;

          return (
            <label
              key={optionValue}
              className={`
                flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer 
                transition-all duration-200
                ${isSelected ? "bg-gray-50" : "bg-white hover:bg-gray-50"}
                ${disabled ? "opacity-50 cursor-not-allowed" : ""}
              `}
              style={{
                borderColor: isSelected ? config.color : "#E5E7EB",
              }}
            >
              <input
                type="radio"
                name="recruitmentStatus"
                value={optionValue}
                checked={isSelected}
                onChange={() => !disabled && onChange(optionValue)}
                disabled={disabled}
                className="mt-1 cursor-pointer accent-orange-500"
              />

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: config.bgColor }}
                  >
                    <IconComponent className="w-4 h-4 text-white" />
                  </div>
                  <span
                    className="font-bold text-sm uppercase space-grotesk"
                    style={{ color: config.color }}
                  >
                    {config.label}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-manrope">
                  {config.description}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );

  // Wrap in highlighted container for edit mode
  if (isEditMode) {
    return (
      <div className="border-2 border-amber-200 bg-amber-50 rounded-lg p-4">
        {content}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
      {content}
    </div>
  );
}

